import type { Workspace } from "./types";
import { predictAll } from "./predict";

/* The Cockpit — client mirror of api/src/reporting/cockpit.ts. One prioritized
 * daily worklist fused from churn predictions, renewals, open risk signals,
 * overdue tasks, and expansion openings, each scored by urgency × value with
 * the reason and recommended next action. Byte-compatible with the API. */

export type WorkKind = "churn" | "renewal" | "signal" | "task" | "expansion";
export type Urgency = "now" | "soon" | "watch";

export interface WorkItem {
  id: string; kind: WorkKind; accountId: string; accountName: string; ownerId: string; arr: number;
  priority: number; urgency: Urgency; title: string; reason: string; action: string; due?: string;
}
export interface Cockpit {
  items: WorkItem[];
  summary: { now: number; soon: number; watch: number; total: number; arrNow: number; byKind: Record<WorkKind, number> };
}

const DAY = 86_400_000;
const arrBoost = (arr: number) => Math.min(15, arr / 50_000);

export interface CockpitOpts { ownerId?: string; today?: string; limit?: number; }

export function buildCockpit(ws: Workspace, opts: CockpitOpts = {}): Cockpit {
  const today = opts.today ?? "2026-08-20";
  const now = +new Date(today);
  const preds = new Map(predictAll(ws, today).map((p) => [p.accountId, p]));
  const acct = new Map(ws.accounts.map((a) => [a.id, a]));
  const items: WorkItem[] = [];

  for (const p of preds.values()) {
    if (p.churnRisk < 60) continue;
    const d = p.churnDrivers[0];
    items.push({
      id: `churn:${p.accountId}`, kind: "churn", accountId: p.accountId, accountName: p.accountName,
      ownerId: p.ownerId, arr: p.arr, priority: Math.round(p.churnRisk + arrBoost(p.arr)),
      urgency: p.churnRisk >= 75 ? "now" : "soon",
      title: `${p.accountName} at ${p.churnRisk}% churn risk`,
      reason: d ? `Top driver: ${d.label} (${d.detail})` : "Elevated churn risk",
      action: "Open a save play and re-engage the sponsor",
    });
  }

  for (const r of ws.renewals) {
    if (r.stage === "closed_won" || r.stage === "closed_lost") continue;
    const days = (+new Date(r.renewalDate) - now) / DAY;
    if (days < 0 || days > 90) continue;
    const a = acct.get(r.accountId);
    const risk = preds.get(r.accountId)?.churnRisk ?? 0;
    const priority = Math.round(50 + (90 - days) * 0.28 + arrBoost(r.arr) + risk * 0.15);
    items.push({
      id: `renewal:${r.id}`, kind: "renewal", accountId: r.accountId, accountName: r.accountName,
      ownerId: r.ownerId ?? a?.ownerId ?? "", arr: r.arr, priority,
      urgency: days <= 30 ? "now" : days <= 60 ? "soon" : "watch",
      title: `${r.accountName} renews in ${Math.round(days)} days`,
      reason: `${r.category} · stage ${r.stage.replace(/_/g, " ")}${risk >= 60 ? ` · churn risk ${risk}%` : ""}`,
      action: "Confirm the renewal path and send the proposal",
      due: r.renewalDate,
    });
  }

  for (const s of ws.signals) {
    if (s.status !== "open" || s.band !== "risk") continue;
    const a = acct.get(s.accountId);
    const mag = Math.abs(s.impact);
    items.push({
      id: `signal:${s.id}`, kind: "signal", accountId: s.accountId, accountName: s.accountName,
      ownerId: a?.ownerId ?? "", arr: a?.arr ?? 0, priority: Math.round(48 + mag + arrBoost(a?.arr ?? 0)),
      urgency: mag >= 12 ? "now" : "soon",
      title: s.title,
      reason: `${s.source} · ${s.detail}`,
      action: s.suggestedPlaybook ? `Run "${s.suggestedPlaybook}"` : "Triage and acknowledge the signal",
    });
  }

  for (const t of ws.tasks) {
    if (t.status === "done") continue;
    const days = (+new Date(t.due) - now) / DAY;
    if (days > 14) continue;
    const a = acct.get(t.accountId);
    const base = t.priority === "high" ? 58 : t.priority === "medium" ? 46 : 36;
    const overdue = days < 0 ? Math.min(20, -days) : 0;
    items.push({
      id: `task:${t.id}`, kind: "task", accountId: t.accountId, accountName: t.accountName,
      ownerId: t.ownerId, arr: a?.arr ?? 0, priority: Math.round(base + overdue),
      urgency: days < 0 ? "now" : days <= 3 ? "soon" : "watch",
      title: t.title,
      reason: days < 0 ? `Overdue by ${Math.round(-days)} days` : `Due in ${Math.round(days)} days`,
      action: "Complete or reschedule this task",
      due: t.due,
    });
  }

  for (const p of preds.values()) {
    if (p.expansion < 60) continue;
    const d = p.expansionDrivers[0];
    items.push({
      id: `expansion:${p.accountId}`, kind: "expansion", accountId: p.accountId, accountName: p.accountName,
      ownerId: p.ownerId, arr: p.arr, priority: Math.round(p.expansion * 0.7 + arrBoost(p.arr)),
      urgency: p.expansion >= 80 ? "soon" : "watch",
      title: `${p.accountName} is ${p.expansion}% likely to expand`,
      reason: d ? `Top driver: ${d.label} (${d.detail})` : "Strong expansion signals",
      action: "Line up an expansion conversation",
    });
  }

  let ranked = items.sort((a, b) => b.priority - a.priority);
  if (opts.ownerId) ranked = ranked.filter((i) => i.ownerId === opts.ownerId);
  if (opts.limit) ranked = ranked.slice(0, opts.limit);

  const byKind = { churn: 0, renewal: 0, signal: 0, task: 0, expansion: 0 } as Record<WorkKind, number>;
  for (const i of ranked) byKind[i.kind]++;
  const summary = {
    now: ranked.filter((i) => i.urgency === "now").length,
    soon: ranked.filter((i) => i.urgency === "soon").length,
    watch: ranked.filter((i) => i.urgency === "watch").length,
    total: ranked.length,
    arrNow: ranked.filter((i) => i.urgency === "now").reduce((n, i) => n + i.arr, 0),
    byKind,
  };
  return { items: ranked, summary };
}
